import React, { useEffect, useMemo, useState } from "react";
import { Table } from "antd";
import dayjs from "dayjs";

interface MaterialInfoSectionProps {
  materials?: any[];
}

const formatDate = (date: string) => {
  if (!date) return "--";
  const d = dayjs(date);
  return d.isValid() ? d.format("DD-MMM-YYYY") : date;
};

const formatCurrency = (value: number) =>
  value
    ? value.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "--";

const materialColumns = [
  { title: "S.No", dataIndex: "sno", key: "sno", width: 60 },
  { title: "Material", dataIndex: "materialName", key: "materialName", width: 200 },
  { title: "Material Code", dataIndex: "materialCode", key: "materialCode", width: 120 },
  { title: "Quantity", dataIndex: "quantity", key: "quantity", width: 100 },
  { title: "UOM", dataIndex: "uomName", key: "uomName", width: 100 },
  {
    title: "Delivery Lead Days",
    dataIndex: "deliveryLeadDays",
    key: "deliveryLeadDays",
    width: 140,
  },
  { title: "Unit Price", dataIndex: "unitPrice", key: "unitPrice", width: 120 },
  { title: "Installation Charges", dataIndex: "installationCharges", key: "installationCharges", width: 160 },
  {
    title: "Sub Total",
    key: "subTotal",
    width: 140,
    render: (_: any, record: any) => {
      const qty = Number(record.quantity) || 0;
      const unitPrice = Number(record.unitPrice) || 0;
      return formatCurrency(qty * unitPrice);
    },
  },
  {
    title: "Total Installation Charges",
    key: "totalInstallationCharges",
    width: 200,
    render: (_: any, record: any) => {
      const installation = Number(record.installationCharges) || 0;
      if (!installation) return "--";
      const qty = Number(record.quantity) || 0;
      return formatCurrency(qty * installation);
    },
  },
  {
    title: "Taxable Amount",
    key: "taxableAmount",
    width: 150,
    render: (_: any, record: any) => {
      const qty = Number(record.quantity) || 0;
      const unitPrice = Number(record.unitPrice) || 0;
      const installation = Number(record.installationCharges) || 0;
      return formatCurrency(qty * (unitPrice + installation));
    },
  },
  { title: "Tax Type", dataIndex: "taxType", key: "taxType", width: 100 },
  { title: "Tax %", dataIndex: "taxPercentage", key: "taxPercentage", width: 100 },
  {
    title: "Tax",
    key: "tax",
    width: 140,
    render: (_: any, record: any) => {
      const qty = Number(record.quantity) || 0;
      const unitPrice = Number(record.unitPrice) || 0;
      const installation = Number(record.installationCharges) || 0;
      const taxable = qty * (unitPrice + installation);
      const taxPct = Number(record.taxPercentage) || 0;
      return formatCurrency((taxable * taxPct) / 100);
    },
  },
  {
    title: "Total",
    key: "total",
    width: 140,
    render: (_: any, record: any) => {
      const qty = Number(record.quantity) || 0;
      const unitPrice = Number(record.unitPrice) || 0;
      const installation = Number(record.installationCharges) || 0;
      const taxable = qty * (unitPrice + installation);
      const taxPct = Number(record.taxPercentage) || 0;
      const total = taxable + (taxable * taxPct) / 100;
      return formatCurrency(total);
    },
  },
  { title: "Remarks", dataIndex: "remarks", key: "remarks", width: 200 },
];

const MaterialInfoSection: React.FC<MaterialInfoSectionProps> = ({
  materials,
}) => {
  const [screenSize, setScreenSize] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setScreenSize(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const dataSource = (materials || []).map((item, idx) => ({
    key: idx,
    sno: idx + 1,
    materialName: item.materialName || "--",
    materialCode: item.materialCode || "--",
    quantity: item.quantity ?? "--",
    uomName: item.uomName || "--",
    unitPrice: item.bidResponse?.unitPrice != null ? item.bidResponse.unitPrice : "--",
    installationCharges: item.bidResponse?.installationCharges != null ? item.bidResponse.installationCharges : "--",
    taxType: item.bidResponse?.taxType || "--",
    taxPercentage: item.bidResponse?.taxPercentage != null ? item.bidResponse.taxPercentage : "--",
    deliveryLeadDays: item.bidResponse?.deliveryLeadDays ?? "--",
    remarks: item.bidResponse?.remarks || "--",
  }));

  const totals = useMemo(() => {
    let subTotal = 0;
    let installationCharges = 0;
    let taxableAmount = 0;
    let taxAmount = 0;
    let grandTotal = 0;

    dataSource.forEach((record) => {
      const qty = Number(record.quantity) || 0;
      const unitPrice = Number(record.unitPrice) || 0;
      const installation = Number(record.installationCharges) || 0;
      const taxPct = Number(record.taxPercentage) || 0;

      const rowSubTotal = qty * unitPrice;
      const rowInstallation = qty * installation;
      const rowTaxable = qty * (unitPrice + installation);
      const rowTax = (rowTaxable * taxPct) / 100;
      const rowTotal = rowTaxable + rowTax;

      subTotal += rowSubTotal;
      installationCharges += rowInstallation;
      taxableAmount += rowTaxable;
      taxAmount += rowTax;
      grandTotal += rowTotal;
    });

    return { subTotal, installationCharges, taxableAmount, taxAmount, grandTotal };
  }, [dataSource]);

  return (
    <div style={{ marginBottom: 32 }}>
      <div className="table-responsive" style={{ overflowX: "auto" }}>
        <div
          style={{
            width: screenSize - 120 + "px",
            margin: "0px auto 15px auto",
          }}
        >
          <Table
            className="customTable"
            columns={materialColumns.map((col: any) => ({
              ...col,
              onHeaderCell: () => ({ style: { whiteSpace: "nowrap" as const } }),
            }))}
            dataSource={dataSource}
            pagination={
              dataSource.length > 10
                ? {
                    pageSize: 10,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50"],
                    showTotal: (total: number, range: [number, number]) =>
                      `${range[0]}-${range[1]} of ${total} items`,
                  }
                : false
            }
            bordered
            style={{ background: "white" }}
            scroll={{ x: "max-content" }}
            locale={{
              emptyText: (
                <span style={{ color: "#888" }}>No material items found.</span>
              ),
            }}
          />
        </div>
      </div>
      {dataSource.length > 0 && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "12px 16px",
          }}
        >
          <div
            style={{
              minWidth: 340,
              border: "1px solid #f0f0f0",
              borderRadius: 6,
              overflow: "hidden",
            }}
          >
            {[
              { label: "Subtotal", value: totals.subTotal },
              { label: "Installation Charges", value: totals.installationCharges },
              { label: "Taxable Amount", value: totals.taxableAmount },
              { label: "Tax Amount", value: totals.taxAmount },
            ].map((row) => (
              <div
                key={row.label}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "8px 16px",
                  borderBottom: "1px solid #f0f0f0",
                  fontSize: 13,
                  color: "#555",
                }}
              >
                <span>{row.label}</span>
                <span>
                  ₹{row.value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            ))}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "10px 16px",
                background: "#f8f9fa",
                fontSize: 14,
                fontWeight: 700,
                color: "#1a1a1a",
              }}
            >
              <span>Grand Total</span>
              <span>
                ₹{totals.grandTotal.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialInfoSection;
